'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface UserProfile {
  id: string;
  full_name: string;
  email: string;
  phone_number?: string;
  role: 'student' | 'admin' | 'instructor';
  avatar_url?: string;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string, role?: 'student' | 'admin') => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<{ error: Error | null }>;
  isAdmin: () => boolean;
  isInstructor: () => boolean;
  isStudent: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Track current user ID to prevent unnecessary state updates
  const currentUserIdRef = React.useRef<string | null>(null);
  const profileFetchedRef = React.useRef<boolean>(false);

  useEffect(() => {
    // Check active sessions - convert async to promise chain
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        const newUserId = session?.user?.id ?? null;
        
        // Only update if user actually changed
        if (currentUserIdRef.current !== newUserId) {
          currentUserIdRef.current = newUserId;
          setUser(session?.user ?? null);
          
          if (session?.user) {
            profileFetchedRef.current = false;
            fetchUserProfile(session.user.id);
          } else {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const newUserId = session?.user?.id ?? null;
      
      // Only update if user actually changed
      if (currentUserIdRef.current !== newUserId) {
        currentUserIdRef.current = newUserId;
        setUser(session?.user ?? null);
        
        if (session?.user) {
          profileFetchedRef.current = false;
          fetchUserProfile(session.user.id);
        } else {
          setUserProfile(null);
          setLoading(false);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Convert async function to promise chain pattern
  const fetchUserProfile = (userId: string) => {
    // Prevent duplicate fetches
    if (profileFetchedRef.current) {
      setLoading(false);
      return;
    }
    profileFetchedRef.current = true;
    
    supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single()
      .then(({ data, error }) => {
        if (error) {
          if (error.code === 'PGRST116') {
            console.log('User profile not found, it may still be creating via trigger');
            // Retry after delay
            setTimeout(() => {
              supabase
                .from('user_profiles')
                .select('*')
                .eq('id', userId)
                .single()
                .then(({ data: retryData, error: retryError }) => {
                  if (retryError) {
                    console.log('Error fetching user profile on retry:', retryError);
                  } else {
                    setUserProfile(retryData);
                  }
                  setLoading(false);
                })
                .catch((err) => {
                  console.log('Exception on retry:', err);
                  setLoading(false);
                });
            }, 1000);
          } else {
            console.log('Error fetching user profile:', error);
            setLoading(false);
          }
        } else {
          setUserProfile(data);
          setLoading(false);
        }
      })
      .catch((error: any) => {
        if (error?.message?.includes('Failed to fetch') || 
            error?.message?.includes('NetworkError')) {
          console.log('Cannot connect to database. Please check your Supabase project status.');
        } else {
          console.log('Error fetching user profile:', error);
        }
        setLoading(false);
      });
  };

  const signUp = (email: string, password: string, fullName: string, role: 'student' | 'admin' = 'student'): Promise<{ error: AuthError | null }> => {
    return supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/signin`,
        data: {
          full_name: fullName,
          role: role,
        },
      },
    })
      .then(({ data, error }) => {
        if (error) return { error };
        return { error: null };
      })
      .catch((error) => {
        return { error: error as AuthError };
      });
  };

  const signIn = (email: string, password: string): Promise<{ error: AuthError | null }> => {
    return supabase.auth.signInWithPassword({
      email,
      password,
    })
      .then(({ data, error }) => {
        if (error) return { error };
        return { error: null };
      })
      .catch((error) => {
        return { error: error as AuthError };
      });
  };

  const signOut = (): Promise<void> => {
    return supabase.auth.signOut()
      .then(() => {
        setUser(null);
        setUserProfile(null);
      });
  };

  const resetPassword = (email: string): Promise<{ error: Error | null }> => {
    return supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    })
      .then(({ error }) => {
        if (error) return { error: error as Error };
        return { error: null };
      })
      .catch((error) => {
        return { error: error as Error };
      });
  };

  const updatePassword = (newPassword: string): Promise<{ error: Error | null }> => {
    return supabase.auth.updateUser({
      password: newPassword,
    })
      .then(({ error }) => {
        if (error) return { error: error as Error };
        return { error: null };
      })
      .catch((error) => {
        return { error: error as Error };
      });
  };

  const updateProfile = (updates: Partial<UserProfile>): Promise<{ error: Error | null }> => {
    if (!user) return Promise.resolve({ error: new Error('No user logged in') });

    return supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', user.id)
      .then(({ error }) => {
        if (error) return { error: error as Error };
        fetchUserProfile(user.id);
        return { error: null };
      })
      .catch((error) => {
        return { error: error as Error };
      });
  };

  const isAdmin = useCallback(() => {
    return userProfile?.role === 'admin';
  }, [userProfile?.role]);

  const isInstructor = useCallback(() => {
    return userProfile?.role === 'instructor';
  }, [userProfile?.role]);

  const isStudent = useCallback(() => {
    return userProfile?.role === 'student';
  }, [userProfile?.role]);

  const value = {
    user,
    userProfile,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
    isAdmin,
    isInstructor,
    isStudent,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
